/**
 * Reminders — what chases, and when (FR-K4): the rules, each with its switch,
 * and what is going out next as a table (the kit's `DataTable`, Wave 15E).
 * Cancelling a scheduled reminder asks first (ADR-0033 §1); it did not before
 * Wave 15.
 */

import { Alert, Badge, Button, ConfirmDialog, EmptyState, Switch, formatDate, formatDateTime } from '@fiveox/ui'
import { Bell } from 'lucide-react'
import { useMemo, useState } from 'react'

import { type ListColumn, ListTable, ListToolbar, useListTable } from '../../components/page'
import { ApiError } from '../../lib/api'
import { type ReminderRecord, type RuleRecord, comms, describeOffset } from '../../lib/api/comms'
import { type Act, SUBJECT_LABELS } from './shared'

export function RuleList({
  rules,
  reminders,
  stuck,
  mayEdit,
  act,
  onCancelled,
}: {
  rules: RuleRecord[]
  reminders: ReminderRecord[]
  stuck: ReminderRecord[]
  mayEdit: boolean
  act: Act
  onCancelled: (said: string) => Promise<void>
}) {
  const scheduled = useMemo(() => reminders.filter((r) => r.status === 'scheduled'), [reminders])
  const [cancelling, setCancelling] = useState<ReminderRecord | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const cancel = async () => {
    if (!cancelling) return
    setBusy(true)
    setError(null)
    try {
      await comms.cancelReminder(cancelling.id)
      setCancelling(null)
      await onCancelled('Cancelled.')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That reminder could not be cancelled.')
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<ListColumn<ReminderRecord>[]>(
    () => [
      {
        key: 'send_at',
        label: 'When',
        width: 170,
        phone: true,
        sortField: 'send_at',
        render: (r) => <span className="whitespace-nowrap">{formatDateTime(r.send_at)}</span>,
      },
      {
        key: 'subject_kind',
        label: 'About',
        width: 220,
        phone: true,
        sortField: 'subject_kind',
        render: (r) => (
          <span className="block max-w-[8rem] whitespace-normal sm:max-w-none">
            {SUBJECT_LABELS[r.subject_kind]}
          </span>
        ),
      },
      {
        key: 'due_on',
        label: 'Due',
        width: 120,
        sortField: 'due_on',
        render: (r) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {r.due_on ? formatDate(r.due_on) : '—'}
          </span>
        ),
      },
    ],
    [],
  )

  const list = useListTable(scheduled, {
    columns,
    filters: [
      {
        key: 'about',
        label: 'About',
        allLabel: 'Anything',
        options: (Object.keys(SUBJECT_LABELS) as ReminderRecord['subject_kind'][]).map((kind) => ({
          value: kind,
          label: SUBJECT_LABELS[kind],
        })),
        match: (r, value) => r.subject_kind === value,
      },
    ],
    initialSort: { field: 'send_at', dir: 'asc' },
  })

  return (
    <div className="space-y-6">
      {rules.length === 0 ? (
        <EmptyState variant="outline" title="No reminder rules yet" />
      ) : (
        <ul className="border-border divide-border divide-y rounded-2xl border bg-card shadow-sm">
          {rules.map((rule) => (
            <li
              key={rule.id}
              className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 sm:px-5"
            >
              <div className="min-w-0 flex-1">
                <p className="text-foreground flex items-center gap-2 text-sm font-medium">
                  <Bell size={14} aria-hidden className="text-muted-foreground" /> {rule.name}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  {rule.kind === 'document_request'
                    ? `Every ${rule.interval_days} days while it is still outstanding, at most ${rule.max_count} times.`
                    : rule.offsets.map(describeOffset).join(', ')}
                </p>
              </div>
              <div className="shrink-0">
                {mayEdit ? (
                  <Switch
                    checked={rule.active}
                    onChange={() =>
                      void act(() => comms.updateRule(rule.id, { active: !rule.active }))
                    }
                    label={rule.active ? 'On' : 'Off'}
                    aria-label={`${rule.name}: ${rule.active ? 'switch off' : 'switch on'}`}
                  />
                ) : rule.active ? (
                  <Badge tone="success">On</Badge>
                ) : (
                  <Badge>Off</Badge>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {stuck.length > 0 && (
        <Alert tone="warning">
          {stuck.length} reminder{stuck.length === 1 ? '' : 's'} started going out and never
          finished. They are not retried automatically, because nobody can tell whether they were
          delivered.
        </Alert>
      )}

      <section className="space-y-3" aria-labelledby="going-out-next">
        <h2 id="going-out-next" className="text-foreground text-base font-semibold">
          Going out next <span className="text-muted-foreground font-normal">({scheduled.length})</span>
        </h2>
        {scheduled.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nothing is scheduled. Switch a rule on, then press “Refresh what is due”.
          </p>
        ) : (
          <>
            <ListToolbar list={list} />
            <ListTable
              list={list}
              loading={false}
              rowKey={(r) => r.id}
              trailing={
                mayEdit
                  ? {
                      header: <span className="sr-only">Cancel</span>,
                      width: 100,
                      cell: (r) => (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setError(null)
                            setCancelling(r)
                          }}
                        >
                          Cancel
                        </Button>
                      ),
                    }
                  : undefined
              }
            />
          </>
        )}
      </section>

      <ConfirmDialog
        open={cancelling !== null}
        onClose={() => setCancelling(null)}
        title="Cancel this reminder?"
        confirmLabel="Cancel the reminder"
        cancelLabel="Keep it"
        tone="danger"
        onConfirm={() => void cancel()}
        busy={busy}
        busyLabel="Cancelling…"
        error={error}
      >
        {cancelling && (
          <p>
            {SUBJECT_LABELS[cancelling.subject_kind]}, due to go out{' '}
            {formatDateTime(cancelling.send_at)}. It will not be sent.
          </p>
        )}
      </ConfirmDialog>
    </div>
  )
}
